import React, { useState, useEffect, useRef } from 'react';
import './QCStandardsManager.css';

const API_BASE_URL = (import.meta as any).env?.VITE_API_URL ?? '';

interface QCStandard {
  id: number;
  name: string;
  source_file: string | null;
  sheet_name: string | null;
  description: string | null;
  dimensions: any[] | null;
  is_active: boolean;
  created_at: string | null;
}

interface QCStandardsManagerProps {
  onSelectStandard?: (standard: QCStandard | null) => void;
  selectedStandardId?: number | null;
}

export const QCStandardsManager: React.FC<QCStandardsManagerProps> = ({
  onSelectStandard,
  selectedStandardId
}) => {
  const [standards, setStandards] = useState<QCStandard[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchStandards();
  }, []);

  const fetchStandards = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/qc-standards`);
      if (!response.ok) throw new Error('获取质检标准失败');
      const data = await response.json();
      setStandards(data.standards || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : '未知错误');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (file: File) => {
    setUploading(true);
    setError(null);
    
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${API_BASE_URL}/api/qc-standards/upload`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.detail || '上传失败');
      }

      const result = await response.json();
      alert(`成功解析 ${result.standards.length} 个质检标准`);
      fetchStandards();
    } catch (err) {
      setError(err instanceof Error ? err.message : '上传失败');
    } finally {
      setUploading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleUpload(file);
    }
  };

  return (
    <div className="qc-standards-manager">
      <div className="qc-standards-header">
        <h3>质检标准管理</h3>
        <button
          className="qc-upload-btn"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
        >
          {uploading ? '解析中...' : '上传Excel'}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
      </div>

      {error && <div className="qc-standards-error">{error}</div>}

      <div className="qc-standards-list">
        <div
          className={`qc-standard-item ${selectedStandardId === null ? 'selected' : ''}`}
          onClick={() => onSelectStandard?.(null)}
        >
          <div className="qc-standard-info">
            <span className="qc-standard-name">ACE CX QA (默认)</span>
            <span className="qc-standard-desc">内置ACE评价体系</span>
          </div>
          <div className="qc-standard-badge default">默认</div>
        </div>

        {loading ? (
          <div className="qc-standards-loading">加载中...</div>
        ) : (
          standards.map((standard) => (
            <div
              key={standard.id}
              className={`qc-standard-item ${selectedStandardId === standard.id ? 'selected' : ''}`}
              onClick={() => onSelectStandard?.(standard)}
            >
              <div className="qc-standard-info">
                <span className="qc-standard-name">{standard.name}</span>
                <span className="qc-standard-desc">
                  {standard.source_file} {standard.sheet_name && `/ ${standard.sheet_name}`}
                </span>
                {standard.dimensions && (
                  <span className="qc-standard-dims">
                    {standard.dimensions.length} 个评估维度
                  </span>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};
